import React, { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import css from "./OrdersOrdersMenuSearchComponent.module.css";
import { ordersActions } from "../../../store/slices/orders-slices";
import { paginationActions } from "../../../store/slices/pagination-slices";
import { OrdersCreateModal } from "../ordersSectionModals";

const SEARCH_DEBOUNCE_MS = 400;

const SEARCH_PLACEHOLDERS = {
  orderId: "Search by order ID",
  clientName: "Search by client name",
  phoneNumber: "Search by phone number",
};

const OrdersOrdersMenuSearchComponent = ({ setIsOpenImportOrdersModal }) => {
  const dispatch = useDispatch();
  const { register, setValue, watch, getValues } = useForm({
    defaultValues: {
      ordersSearch: "",
      searchField: "clientName",
    },
  });
  const { filterParams } = useSelector((store) => store.orders);
  const filterParamsRef = useRef(filterParams);
  filterParamsRef.current = filterParams;

  const searchField = watch("searchField");
  const ordersSearch = watch("ordersSearch");

  const debounceTimerRef = useRef(null);
  const skipInitialSearchDebounceRef = useRef(true);
  const isFirstSearchFieldEffectRef = useRef(true);

  useEffect(() => {
    setValue("ordersSearch", "");
    setValue("searchField", "clientName");
    dispatch(ordersActions.setFilterParams({ word: "", searchField: "clientName" }));
  }, [setValue, dispatch]);

  const runSearch = useCallback(() => {
    const data = getValues();
    const word = data.ordersSearch ?? "";
    const field = data.searchField ?? "clientName";

    dispatch(paginationActions.changePage(1));

    dispatch(
      ordersActions.setFilterParams({
        word,
        searchField: field,
      })
    );
    dispatch(
      ordersActions.getAllOrders({
        page: 1,
        ...filterParamsRef.current,
        word,
        searchField: field,
      })
    );
  }, [dispatch, getValues]);

  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  useEffect(() => {
    if (skipInitialSearchDebounceRef.current) {
      skipInitialSearchDebounceRef.current = false;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      runSearchRef.current();
      debounceTimerRef.current = null;
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [ordersSearch]);

  useEffect(() => {
    if (isFirstSearchFieldEffectRef.current) {
      isFirstSearchFieldEffectRef.current = false;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    runSearchRef.current();
  }, [searchField]);

  return (
    <div className={css.wrap}>
      <div className={css.createButtonWrapper}>
        <OrdersCreateModal />
      </div>
      <button
        type="button"
        className={css.importBtn}
        onClick={() => setIsOpenImportOrdersModal(true)}
      >
        import
      </button>

      <form className={css.form}>
        <select
          {...register("searchField")}
          className={css.fieldSelect}
          aria-label="Search field"
        >
          <option value="clientName">Client name</option>
          <option value="orderId">Order ID</option>
          <option value="phoneNumber">Phone number</option>
        </select>
        <input
          type="text"
          {...register("ordersSearch")}
          placeholder={
            SEARCH_PLACEHOLDERS[searchField] ?? SEARCH_PLACEHOLDERS.clientName
          }
        />
      </form>
    </div>
  );
};

export { OrdersOrdersMenuSearchComponent };
